export interface DebugApiMeta {
  id: number;
  key: string;
  method: string;
  url: string;
  label?: string;
  startedAt: number;
  count: number;
}

const DEBUG_API_KEY = 'autoqm_debug_api';
const DEBUG_API_FILTER_KEY = 'autoqm_debug_api_filter';
const DEBUG_API_STACK_KEY = 'autoqm_debug_api_stack';

const safeLocalStorageGet = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const getSearchParam = (key: string): string | null => {
  try {
    return new URLSearchParams(window.location.search).get(key);
  } catch {
    return null;
  }
};

const isTruthyParam = (value: string | null): boolean =>
  value === '1' || value === 'true' || value === 'yes';

export const isDebugApiEnabled = (): boolean => {
  if (!import.meta.env.DEV) return false;
  const query = getSearchParam('debugApi');
  if (query !== null) return isTruthyParam(query);
  return safeLocalStorageGet(DEBUG_API_KEY) === '1';
};

const shouldCaptureStack = (): boolean => {
  const query = getSearchParam('debugApiStack');
  if (query !== null) return isTruthyParam(query);
  const stored = safeLocalStorageGet(DEBUG_API_STACK_KEY);
  if (stored !== null) return stored === '1';
  return true;
};

const getFilterList = (): string[] => {
  const raw =
    getSearchParam('debugApiFilter') || safeLocalStorageGet(DEBUG_API_FILTER_KEY);
  if (!raw) return [];
  return raw
    .split(/[, \n\r\t]+/)
    .map((item) => item.trim())
    .filter(Boolean);
};

const matchesFilter = (url: string, label?: string): boolean => {
  const filters = getFilterList();
  if (filters.length === 0) return true;
  const target = `${url} ${label ?? ''}`.toLowerCase();
  return filters.some((token) => target.includes(token.toLowerCase()));
};

const summarizeValue = (value: unknown): unknown => {
  if (!value) return value;
  if (value instanceof FormData) return '[FormData]';
  if (value instanceof Blob) return `[Blob ${value.size}]`;
  if (value instanceof ArrayBuffer) return `[ArrayBuffer ${value.byteLength}]`;
  if (ArrayBuffer.isView(value)) return `[TypedArray ${value.byteLength}]`;
  if (typeof value === 'string') {
    return value.length > 500 ? `${value.slice(0, 500)}… (${value.length})` : value;
  }
  if (typeof value === 'object') {
    try {
      return JSON.parse(JSON.stringify(value));
    } catch {
      return '[Unserializable]';
    }
  }
  return value;
};

const formatStack = (stack: string): string => {
  return stack
    .split('\n')
    .slice(2)
    .filter((line) => !line.includes('debugApi'))
    .join('\n');
};

let debugSeq = 0;
const debugCounts = new Map<string, number>();

export const startApiDebug = (info: {
  method?: string;
  url?: string;
  label?: string;
  params?: unknown;
  data?: unknown;
  source?: string;
}): DebugApiMeta | null => {
  if (!isDebugApiEnabled()) return null;

  const url = info.url ?? '';
  if (!matchesFilter(url, info.label)) return null;

  const method = (info.method || 'GET').toUpperCase();
  const key = `${method} ${url}`;
  const count = (debugCounts.get(key) ?? 0) + 1;
  debugCounts.set(key, count);

  const id = ++debugSeq;
  const startedAt =
    typeof performance !== 'undefined' && performance.now
      ? performance.now()
      : Date.now();

  const labelText = info.label ? ` (${info.label})` : '';
  console.groupCollapsed(`[debug-api #${id} ${count}] ${method} ${url}${labelText}`);
  if (info.source) console.log('source', info.source);
  if (info.params) console.log('params', summarizeValue(info.params));
  if (info.data) console.log('data', summarizeValue(info.data));
  if (shouldCaptureStack()) {
    const stack = new Error().stack;
    if (stack) console.log('stack', formatStack(stack));
  }

  return {
    id,
    key,
    method,
    url,
    label: info.label,
    startedAt,
    count,
  };
};

export const finishApiDebug = (
  meta: DebugApiMeta | null | undefined,
  result?: { status?: number } | Error
): void => {
  if (!meta) return;
  if ((meta as DebugApiMeta & { __finished?: boolean }).__finished) return;
  (meta as DebugApiMeta & { __finished?: boolean }).__finished = true;

  const endedAt =
    typeof performance !== 'undefined' && performance.now
      ? performance.now()
      : Date.now();
  const durationMs = Math.round(endedAt - meta.startedAt);

  if (result instanceof Error) {
    console.log('error', result);
  } else if (result && typeof result === 'object' && 'status' in result) {
    console.log('status', result.status);
  }
  console.log('duration', `${durationMs}ms`);
  console.groupEnd();
};

export const debugEvent = (label: string, data?: unknown): void => {
  if (!isDebugApiEnabled()) return;
  if (data !== undefined) {
    console.log(`[debug-event] ${label}`, data);
    return;
  }
  console.log(`[debug-event] ${label}`);
};
